import React, { useEffect, useMemo, useState } from "react";
import CrudModule from "@/components/admin/CrudModule";
import { Badge } from "@/components/common/Primitives";
import { membersCrud } from "@/services/membersService";
import { unitsCrud } from "@/services/unitsService";

const MEMBER_EXPORT_COLUMNS = [
  { key: "membership_no", label: "Membership No." },
  { key: "full_name", label: "Full Name" },
  { key: "unit_slug", label: "Unit Slug" },
  { key: "membership_type", label: "Membership Type" },
  { key: "status", label: "Status" },
  { key: "joined_year", label: "Joined Year" },
];

export default function AdminMembersPage() {
  const [units, setUnits] = useState([]);

  useEffect(() => {
    unitsCrud
      .list()
      .then((rows) => {
        setUnits(
          rows.sort(
            (a, b) =>
              (a.sort_order || 0) - (b.sort_order || 0) ||
              (a.name || "").localeCompare(b.name || "")
          )
        );
      })
      .catch(() => setUnits([]));
  }, []);

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.slug, label: u.name || u.slug })),
    [units]
  );

  const unitsBySlug = useMemo(
    () => Object.fromEntries(units.map((u) => [u.slug, u.name || u.slug])),
    [units]
  );

  const fields = useMemo(
    () => [
      { name: "membership_no", label: "Membership Number", type: "text", required: true, placeholder: "BCS-10001" },
      { name: "full_name", label: "Full Name", type: "text", required: true },
      {
        name: "unit_slug",
        label: "Linked Unit",
        type: "select",
        options: unitOptions,
        required: true,
        hint: "Select the parish unit this member belongs to.",
      },
      { name: "membership_type", label: "Membership Type", type: "select", options: ["Life Member", "Ordinary Member"], required: true },
      { name: "status", label: "Status", type: "select", options: ["Active", "Lapsed"], required: true },
      { name: "joined_year", label: "Joined Year", type: "number" },
    ],
    [unitOptions]
  );

  const columns = useMemo(
    () => [
      { key: "membership_no", label: "Membership No." },
      { key: "full_name", label: "Name" },
      {
        key: "unit_slug",
        label: "Unit",
        render: (r) => unitsBySlug[r.unit_slug] || r.unit_slug || "—",
      },
      { key: "joined_year", label: "Since" },
      { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "Active" ? "green" : "gray"}>{r.status}</Badge> },
    ],
    [unitsBySlug]
  );

  return (
    <CrudModule
      title="Members"
      description="Membership records powering the public membership search. Only non-sensitive fields are exposed publicly."
      crud={membersCrud}
      searchKeys={["membership_no", "full_name", "unit_slug"]}
      exportCsv={{
        filename: "bcs-members",
        columns: MEMBER_EXPORT_COLUMNS,
      }}
      columns={columns}
      fields={fields}
    />
  );
}
